/**
 * StudioPopover — the single command-bar popover primitive.
 *
 * The bar previously ran two paradigms that looked identical but behaved
 * differently: an ARIA menu (Draw/Place/Assist/Overlays/Data) and a dialog
 * panel (Parcel/Parameters/Exclusions). Same trigger, different keyboard and
 * focus contracts — an inconsistency users felt and screen readers announced
 * two ways.
 *
 * This unifies the trigger, the positioning engine and the dismissal contract
 * (Escape restores focus to the trigger, outside pointer-down and viewport
 * resize close). It deliberately keeps TWO content roles, because that
 * distinction is real:
 *
 *   variant="menu"  → role="menu", narrow, focuses its first item on open.
 *                     For lists of commands.
 *   variant="panel" → role="dialog", wide + scrollable, no autofocus steal.
 *                     For form controls — inputs inside a role="menu" is an
 *                     accessibility violation, not a style choice.
 *
 * Positioning is `fixed`, so the bar's horizontal scroll container can never
 * clip an open popover.
 */
import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { ChevronDown, type LucideIcon } from 'lucide-react';

export type StudioPopoverVariant = 'menu' | 'panel';

const VARIANT_WIDTH_PX: Record<StudioPopoverVariant, number> = {
  menu: 260,
  panel: 340,
};

export interface StudioPopoverProps {
  label: string;
  /** Accessible name for the surface itself (menu/dialog). */
  surfaceLabel: string;
  icon: LucideIcon;
  variant?: StudioPopoverVariant;
  active?: boolean;
  disabled?: boolean;
  testId: string;
  /** Receives a `close` callback so items can dismiss the popover. */
  children: (close: () => void) => React.ReactNode;
}

export const StudioPopover: React.FC<StudioPopoverProps> = ({
  label,
  surfaceLabel,
  icon: Icon,
  variant = 'menu',
  active = false,
  disabled = false,
  testId,
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ left: 8, top: 48 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const surfaceId = useId();

  const close = (): void => setOpen(false);

  useLayoutEffect(() => {
    if (!open) return;
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPosition({
      left: Math.max(8, Math.min(rect.left, window.innerWidth - VARIANT_WIDTH_PX[variant] - 8)),
      top: rect.bottom + 4,
    });
  }, [open, variant]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (triggerRef.current?.contains(target) || surfaceRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onResize = (): void => setOpen(false);
    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  useEffect(() => {
    // Menus move focus onto their first command; panels hold form controls, so
    // stealing focus would fight the user's own tab order.
    if (open && variant === 'menu') {
      surfaceRef.current?.querySelector('button')?.focus();
    }
  }, [open, variant]);

  return (
    <div className="shrink-0">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup={variant === 'menu' ? 'menu' : 'dialog'}
        aria-expanded={open}
        aria-controls={surfaceId}
        disabled={disabled}
        onClick={() => setOpen(value => !value)}
        className={`flex min-h-8 items-center gap-1.5 rounded-md px-2 py-1 text-[10px] font-semibold tracking-wide transition-colors focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40 max-[599px]:min-h-11 ${
          active || open
            ? 'bg-primary/15 text-primary'
            : 'text-text-secondary hover:bg-background hover:text-text-primary'
        }`}
        data-testid={testId}
      >
        <Icon className="h-3.5 w-3.5" aria-hidden />
        {label}
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden />
      </button>
      <div
        ref={surfaceRef}
        id={surfaceId}
        role={variant === 'menu' ? 'menu' : 'dialog'}
        aria-label={surfaceLabel}
        hidden={!open}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.stopPropagation();
            setOpen(false);
            triggerRef.current?.focus();
          }
        }}
        className={`fixed z-[80] rounded-lg border border-border bg-card shadow-2xl shadow-black/40 ${
          variant === 'menu'
            ? 'w-64 space-y-1 p-1.5'
            : 'max-h-[70vh] w-80 overflow-y-auto overscroll-contain p-2.5'
        }`}
        style={{ left: position.left, top: position.top }}
        data-testid={`${testId}-menu`}
      >
        {children(close)}
      </div>
    </div>
  );
};

/** A labelled group of commands inside a `variant="menu"` popover. */
export const StudioPopoverSection: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div role="group" aria-label={label} className="space-y-1">
    <p className="px-1 pb-0.5 pt-1 text-[9px] font-bold uppercase tracking-[0.18em] text-text-muted">
      {label}
    </p>
    {children}
  </div>
);

/** Hairline rule between command groups inside a popover. */
export const StudioPopoverDivider = (): React.JSX.Element => (
  <div role="separator" className="my-1 border-t border-border/70" />
);
